import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function AboutPage() {
  const values = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Trust & Security',
      description: 'Every transaction is protected by our industry-leading Buyer Guarantee and fraud prevention systems.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      title: 'Premium Experience',
      description: 'We curate exceptional experiences, connecting you with the most sought-after events worldwide.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: 'Community First',
      description: 'We empower fans and sellers alike, creating a marketplace where everyone wins.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Innovation',
      description: 'Cutting-edge technology powers seamless transactions and personalized recommendations.'
    }
  ];

  const stats = [
    { value: '10M+', label: 'Tickets Sold' },
    { value: '50K+', label: 'Events' },
    { value: '99.9%', label: 'Satisfaction Rate' },
    { value: '24/7', label: 'Support' }
  ];

  return (
    <>
      <Helmet><title>About Us | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div className="bg-navy py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl md:text-5xl font-bold !text-white mb-4">About BESTIX</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              The luxury ticket marketplace by BESPOKIAN
            </p>
          </div>
        </div>

        {/* Mission */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-playfair font-bold text-navy mb-6">Our Mission</h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              BESTIX exists to connect passionate fans with unforgettable experiences. We believe everyone deserves access to the moments that matter most—whether it's a sold-out concert, a championship game, or an exclusive premiere.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              As the premium ticketing arm of BESPOKIAN, we combine white-glove service with cutting-edge technology to create a marketplace where trust, transparency, and exceptional experiences come standard.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-navy py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-gold mb-2">{stat.value}</div>
                  <div className="text-gray-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-playfair font-bold text-navy text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4 text-gold">
                  {value.icon}
                </div>
                <h3 className="font-semibold text-navy text-lg mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Story */}
        <div className="bg-gray-100 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-playfair font-bold text-navy text-center mb-8">Our Story</h2>
              <div className="prose prose-lg mx-auto text-gray-600">
                <p>
                  BESTIX was born from a simple frustration: buying tickets to live events was broken. Fans faced counterfeit tickets, hidden fees, and poor customer service. Sellers struggled with outdated platforms and unfair commission structures.
                </p>
                <p>
                  We set out to build something better. A marketplace where authenticity is guaranteed, pricing is transparent, and every customer is treated like a VIP. A platform where technology enhances the human experience rather than replacing it.
                </p>
                <p>
                  Today, BESTIX serves millions of fans and thousands of sellers, facilitating access to the world's most sought-after events. From intimate jazz clubs to stadium spectacles, from Broadway premieres to championship finals—we're there, connecting people with the moments that become lifelong memories.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="container mx-auto px-4 py-16">
          <div className="bg-gradient-to-r from-navy to-navy-dark rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-playfair font-bold text-white mb-4">
              Ready to Experience BESTIX?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Join millions of fans who trust us for their most important events.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/events" className="btn btn-primary">
                Browse Events
              </Link>
              <Link to="/sell" className="btn btn-secondary">
                Start Selling
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
